import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { clearExpandedState } from '@/utils/explorerStateStorage'

export interface DatabaseConnection {
  id: string
  name: string
  type: 'mysql' | 'postgresql' | 'redis' | 'redis-cluster' | 'memcached' | 'oracle' | 'mongodb' | 'clickhouse' | 'scylladb' | 'milvus'
  host: string
  port: number
  username: string
  password: string
  database?: string
  ssl?: boolean
  agentId?: string
  createdAt: string
  updatedAt: string
}

// Connection health for the active connection.
// - 'healthy'        : agent reachable and connected
// - 'checking'       : initial probe in flight (just after setActiveConnection)
// - 'agent_offline'  : agent reachable but reports a non-connected status
// - 'error'          : agent status endpoint itself unreachable / failed repeatedly
export type ConnectionHealth = 'healthy' | 'checking' | 'agent_offline' | 'error'

interface ConnectionState {
  connections: DatabaseConnection[]
  activeConnection: DatabaseConnection | null
  // Live health of `activeConnection`. Not persisted.
  connectionHealth: ConnectionHealth
  lastActiveConnectionId: string | null
  // Track last accessed datasource globally
  lastAccessedDatasourceId: string | null
  isConnecting: boolean
  addConnection: (connection: Omit<DatabaseConnection, 'id' | 'createdAt' | 'updatedAt'>) => void
  updateConnection: (id: string, updates: Partial<DatabaseConnection>) => void
  deleteConnection: (id: string) => void
  setActiveConnection: (connection: DatabaseConnection | null) => void
  setConnectionHealth: (health: ConnectionHealth) => void
  setLastAccessedDatasourceId: (datasourceId: string | null) => void
  loadLastActiveConnection: () => void
  testConnection: (connection: Omit<DatabaseConnection, 'id' | 'createdAt' | 'updatedAt'> & { agentId: string }) => Promise<boolean>
}

export const useConnectionStore = create<ConnectionState>()(
  persist(
    (set, get) => ({
      connections: [],
      activeConnection: null,
      connectionHealth: 'healthy',
      lastActiveConnectionId: null,
      lastAccessedDatasourceId: null,
      isConnecting: false,

      addConnection: (connectionData) => {
        const newConnection: DatabaseConnection = {
          ...connectionData,
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        set((state: ConnectionState) => ({
          connections: [...state.connections, newConnection]
        }))
      },

      updateConnection: (id, updates) => {
        set((state: ConnectionState) => ({
          connections: state.connections.map((conn: DatabaseConnection) =>
            conn.id === id
              ? { ...conn, ...updates, updatedAt: new Date().toISOString() }
              : conn
          )
        }))
      },

      deleteConnection: (id) => {
        // Clear expanded state for the deleted connection
        clearExpandedState(id)
        
        set((state: ConnectionState) => {
          const clearingActive = state.activeConnection?.id === id
          return {
            connections: state.connections.filter((conn: DatabaseConnection) => conn.id !== id),
            activeConnection: clearingActive ? null : state.activeConnection,
            connectionHealth: clearingActive ? 'healthy' : state.connectionHealth,
            lastActiveConnectionId: state.lastActiveConnectionId === id ? null : state.lastActiveConnectionId
          }
        })
      },

      setActiveConnection: (connection) => {
        const state = get()
        const previousConnectionId = state.activeConnection?.id

        // Clear expanded state for the previous connection if switching to a different one
        if (previousConnectionId && connection?.id && previousConnectionId !== connection.id) {
          clearExpandedState(previousConnectionId)

          // Clear database users selection for the previous connection
          import('@/stores/databaseUsersSelectionStore').then(({ useDatabaseUsersSelectionStore }) => {
            useDatabaseUsersSelectionStore.getState().clearSelectionForConnection(previousConnectionId)
            return undefined
          }).catch((error) => {
            console.error('Failed to clear database users selection on connection change:', error)
            return undefined
          })
        }

        set({
          activeConnection: connection,
          lastActiveConnectionId: connection?.id || null,
          // Health is 'checking' while the first probe runs; when there's no
          // active connection, reset to 'healthy' so lingering states don't
          // leak between connections.
          connectionHealth: connection ? 'checking' : 'healthy',
        })
      },

      setConnectionHealth: (health) => {
        set({ connectionHealth: health })
      },

      setLastAccessedDatasourceId: (datasourceId) => {
        set({ lastAccessedDatasourceId: datasourceId })
      },

      loadLastActiveConnection: () => {
        const state = get()
        if (state.lastActiveConnectionId) {
          // We'll need to fetch the connection data from the API since we only store the ID
          // This will be handled in the component that calls this function
        }
      },

      testConnection: async (connectionData) => {
        set({ isConnecting: true })
        try {
          // Import APIs
          const { createDatasources, deleteDatasources } = await import('@/services/apiClient')
          const { AgentProxyService } = await import('@/services/agentProxyService')
          
          // Create a temporary data source to test the connection
          // Note: Redis Cluster uses 'Redis' databaseType with clusterNodes in connectionParams
          const requestBody = {
            displayName: `test-${Date.now()}`,
            databaseType: (
              connectionData.type === 'mysql' ? 'MySQL' as const :
              connectionData.type === 'redis' ? 'Redis' as const :
              connectionData.type === 'redis-cluster' ? 'Redis' as const :
              connectionData.type === 'memcached' ? 'Memcached' as const :
              connectionData.type === 'oracle' ? 'OracleDB' as const :
              connectionData.type === 'mongodb' ? 'MongoDB' as const :
              connectionData.type === 'clickhouse' ? 'ClickHouse' as const :
              connectionData.type === 'scylladb' ? 'ScyllaDB' as const :
              connectionData.type === 'milvus' ? 'Milvus' as const :
              'PostgreSQL' as const
            ),
            host: connectionData.host,
            port: connectionData.port,
            // Use 'default' for Redis/RedisCluster/Memcached if username is empty
            username: connectionData.username && connectionData.username.trim()
              ? connectionData.username
              : ((connectionData.type === 'redis' || connectionData.type === 'redis-cluster' || connectionData.type === 'memcached') ? 'default' : connectionData.username),
            password: connectionData.password,
            agentId: connectionData.agentId || '',
            defaultDatabase: connectionData.database || null,
            connectionParams: connectionData.ssl ? { ssl: true } : null,
          }

          const { data: tempDataSource } = await createDatasources({ body: requestBody, throwOnError: true })
          
          if (!tempDataSource.id) {
            console.error('Connection test failed: Failed to create temporary data source')
            set({ isConnecting: false })
            return false
          }
          
          // Test the connection using the temporary data source
          const testResult = await AgentProxyService.testConnection(tempDataSource.id)
          const isSuccessful = testResult && typeof testResult === 'object' && 'connection_successful' in testResult 
            ? Boolean(testResult.connection_successful) 
            : false
          
          // Clean up the temporary data source
          try {
            await deleteDatasources({
              path: { datasource_id: tempDataSource.id },
              query: { test_cleanup: true },
              throwOnError: true,
            })
          } catch (cleanupError) {
            console.warn('Failed to clean up temporary data source:', cleanupError)
          }
          
          set({ isConnecting: false })
          return isSuccessful
        } catch (error) {
          console.error('Connection test failed:', error)
          set({ isConnecting: false })
          
          // Handle specific error types for better user feedback
          if (error instanceof Error) {
            if (error.message.includes('agent') || error.message.includes('Agent')) {
              console.error('Agent-related error during connection test:', error.message)
            } else if (error.message.includes('connection') || error.message.includes('Connection')) {
              console.error('Database connection error during test:', error.message)
            } else if (error.message.includes('authentication') || error.message.includes('Authentication')) {
              console.error('Authentication error during connection test:', error.message)
            }
          }
          
          return false
        }
      },
    }),
    {
      name: 'connection-storage',
      storage: createJSONStorage(() => localStorage),
      // Persist lastActiveConnectionId and lastAccessedDatasourceId, not the full connection data
      partialize: (state) => ({
        lastActiveConnectionId: state.lastActiveConnectionId,
        lastAccessedDatasourceId: state.lastAccessedDatasourceId
      }),
    }
  )
)
